import {publishOptimisticFields} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {minimizeCommentMutation as minimizeCommentMutationQueryId} from '@github-ui/server-persisted-queries'
import type {
  MinimizeCommentMutationMutation,
  ReportedContentClassifiers,
} from '@github-ui/server-persisted-queries/types/minimizeCommentMutation'
import {type JSONRequestInit, reactFetchJSON} from '@github-ui/verified-fetch'
import type {Environment, RecordSourceProxy, RecordSourceSelectorProxy} from 'relay-runtime'

import {beginCommentMinimizationOverlay, type OptimisticConnection} from './comment-connection-overlay'

function minimizationPath(owner?: string, repo?: string): string {
  if (owner && repo) {
    return `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/comments/minimization?queryId=${minimizeCommentMutationQueryId}`
  }

  const [, inferredOwner, inferredRepo] = window.location.pathname.split('/')
  if (!inferredOwner || !inferredRepo) {
    throw new Error('Failed to infer repository path for minimizing comment')
  }

  return `/${encodeURIComponent(inferredOwner)}/${encodeURIComponent(inferredRepo)}/comments/minimization?queryId=${minimizeCommentMutationQueryId}`
}

function writeRelayMinimizationState(
  store: RecordSourceProxy | RecordSourceSelectorProxy,
  id: string,
  fields: {
    isHidden: boolean
    minimizedReason: string | null
    pendingMinimizeReason: string | null
    isMinimized: boolean
  },
) {
  const comment = store.get(id)
  comment?.setValue(fields.isHidden, 'isHidden')
  comment?.setValue(fields.minimizedReason, 'minimizedReason')
  comment?.setValue(fields.pendingMinimizeReason, 'pendingMinimizeReason')
  comment?.setValue(fields.isMinimized, 'isMinimized')
}

export function minimizeCommentMutation({
  environment,
  input: {id, reason, owner, repo},
  onCompleted,
  onError,
  optimisticConnection,
}: {
  environment: Environment
  input: {id: string; reason: ReportedContentClassifiers; owner?: string; repo?: string}
  onCompleted?: () => void
  onError?: (error?: Error) => void
  optimisticConnection?: OptimisticConnection
}) {
  const request: JSONRequestInit = {
    method: 'PUT',
    body: {
      input: {subjectId: id, classifier: reason},
    },
  }
  const path = minimizationPath(owner, repo)
  const optimisticFields = {
    isHidden: true,
    minimizedReason: reason,
    pendingMinimizeReason: reason,
  }
  const publication = publishOptimisticFields(id, optimisticFields)
  const overlay = optimisticConnection
    ? beginCommentMinimizationOverlay({connection: optimisticConnection, nodeId: id, fields: optimisticFields})
    : undefined
  const relayUpdate =
    typeof environment.applyUpdate === 'function'
      ? environment.applyUpdate({
          storeUpdater: store =>
            writeRelayMinimizationState(store, id, {
              isHidden: true,
              minimizedReason: reason,
              pendingMinimizeReason: reason,
              isMinimized: true,
            }),
        })
      : undefined
  const abortController = new AbortController()
  let settled = false
  const fail = (error: Error) => {
    if (settled) return
    settled = true
    relayUpdate?.dispose()
    publication.failed(error)
    overlay?.fail(error)
    onError?.(error)
  }

  void (async () => {
    try {
      const response = await reactFetchJSON(path, {...request, signal: abortController.signal})
      if (!response.ok) {
        throw new Error(`Failed to minimize comment (${response.status})`)
      }
      const payload = (await response.json()) as {
        errors?: Array<{message?: string}>
        data?: MinimizeCommentMutationMutation
      }
      const comment = payload.data?.minimizeComment?.minimizedComment
      if (!comment) {
        throw new Error(payload.errors?.[0]?.message ?? 'Failed to minimize comment')
      }
      const isMinimized = comment.isMinimized
      const isHidden = isMinimized
      if (relayUpdate) {
        environment.commitUpdate(store => {
          writeRelayMinimizationState(store, id, {
            isHidden,
            minimizedReason: comment.minimizedReason ?? null,
            pendingMinimizeReason: comment.minimizedReason ?? null,
            isMinimized,
          })
        })
        relayUpdate.dispose()
      }
      const confirmedFields = {
        isHidden,
        minimizedReason: comment.minimizedReason ?? null,
        pendingMinimizeReason: comment.minimizedReason ?? null,
      }
      publication.completed(confirmedFields)
      overlay?.settle(confirmedFields)
      settled = true
      onCompleted?.()
    } catch (error) {
      if (settled) return
      const normalizedError =
        error instanceof Error
          ? error.name === 'AbortError'
            ? new Error('Minimizing comment was cancelled')
            : error
          : new Error('Failed to minimize comment')
      fail(normalizedError)
    }
  })()

  return {
    dispose: () => {
      abortController.abort()
      fail(new Error('Minimizing comment was cancelled'))
    },
  }
}
